import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Trash2, ShieldCheck, LogOut, Plus, Save, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_KEY = "oz_admin_token";
const EMPTY = { image: "", url: "", caption: "", order: 0, active: true };

export default function AdminInstagram() {
  const [token, setToken] = useState(() => localStorage.getItem(LS_KEY) || "");
  const [input, setInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(EMPTY);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const headers = (tk) => ({ "X-Admin-Token": tk || token });

  const load = useCallback(async (tk) => {
    setLoading(true); setError("");
    try {
      const { data } = await axios.get(`${API}/admin/instagram`, { headers: headers(tk) });
      setPosts(data); setAuthed(true); localStorage.setItem(LS_KEY, tk);
    } catch (e) {
      if (e.response?.status === 401) { setError("Token administrateur invalide."); setAuthed(false); localStorage.removeItem(LS_KEY); }
      else setError("Erreur de chargement.");
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const connect = (e) => { e.preventDefault(); setToken(input.trim()); load(input.trim()); };
  const logout = () => { localStorage.removeItem(LS_KEY); setToken(""); setAuthed(false); setPosts([]); };

  const create = async () => {
    if (!draft.image.trim()) { setError("L'URL de l'image est obligatoire."); return; }
    const { data } = await axios.post(`${API}/admin/instagram`, { ...draft, order: Number(draft.order) || 0 }, { headers: headers() });
    setPosts((p) => [...p, data].sort((a, b) => a.order - b.order));
    setDraft(EMPTY); setError("");
  };
  const save = async (post) => {
    await axios.put(`${API}/admin/instagram/${post.id}`, {
      image: post.image, url: post.url, caption: post.caption, order: Number(post.order) || 0, active: post.active,
    }, { headers: headers() });
    setPosts((p) => [...p].sort((a, b) => a.order - b.order));
  };
  const remove = async (id) => {
    await axios.delete(`${API}/admin/instagram/${id}`, { headers: headers() });
    setPosts((p) => p.filter((x) => x.id !== id));
  };
  const patch = (id, field, value) => setPosts((p) => p.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const field = "w-full rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm outline-none focus:border-[var(--oz-gold)]";

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Galerie Instagram</h1>
        </div>
        <form onSubmit={connect} className="bg-white border border-[var(--oz-border)] rounded-2xl p-6 space-y-4">
          <label className="block text-sm text-[var(--oz-muted)]">Token administrateur</label>
          <input data-testid="admin-token-input" type="password" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Collez votre token admin" className={field} />
          {error && <p data-testid="admin-error" className="text-sm text-red-600">{error}</p>}
          <button data-testid="admin-connect" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors">
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 md:py-16">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[var(--oz-gold)]" />
          <h1 className="font-serif text-2xl text-[var(--oz-text)]">Galerie Instagram</h1>
        </div>
        <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)]">
          <LogOut className="w-4 h-4" /> Déconnexion
        </button>
      </div>

      {/* Ajout */}
      <div className="bg-white border border-[var(--oz-border)] rounded-2xl p-5 mb-8">
        <h2 className="font-serif text-lg text-[var(--oz-text)] mb-3">Ajouter une publication</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="ig-new-image" className={field} placeholder="URL de l'image *" value={draft.image} onChange={(e) => setDraft({ ...draft, image: e.target.value })} />
          <input data-testid="ig-new-url" className={field} placeholder="URL du post Instagram (optionnel)" value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} />
          <input data-testid="ig-new-caption" className={field} placeholder="Légende" value={draft.caption} onChange={(e) => setDraft({ ...draft, caption: e.target.value })} />
          <input data-testid="ig-new-order" type="number" className={field} placeholder="Ordre (0 = premier)" value={draft.order} onChange={(e) => setDraft({ ...draft, order: e.target.value })} />
        </div>
        {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        <button data-testid="ig-add" onClick={create} className="mt-3 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm hover:bg-[var(--oz-gold-hover)]">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[var(--oz-gold)]" /></div>
      ) : (
        <div className="space-y-4">
          {posts.map((p) => (
            <div key={p.id} data-testid={`ig-row-${p.id}`} className="bg-white border border-[var(--oz-border)] rounded-2xl p-4 flex gap-4">
              <img src={p.image} alt={p.caption} className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
              <div className="flex-1 grid sm:grid-cols-2 gap-2">
                <input className={field} value={p.image} onChange={(e) => patch(p.id, "image", e.target.value)} />
                <input className={field} placeholder="URL du post" value={p.url || ""} onChange={(e) => patch(p.id, "url", e.target.value)} />
                <input className={field} placeholder="Légende" value={p.caption || ""} onChange={(e) => patch(p.id, "caption", e.target.value)} />
                <div className="flex gap-2">
                  <input type="number" className={`${field} w-24`} value={p.order} onChange={(e) => patch(p.id, "order", e.target.value)} />
                  <button data-testid={`ig-toggle-${p.id}`} onClick={() => patch(p.id, "active", !p.active)} title={p.active ? "Visible" : "Masquée"}
                    className={`inline-flex items-center gap-1.5 px-3 rounded-lg text-xs font-semibold border ${p.active ? "border-[var(--oz-gold)] text-[var(--oz-gold)]" : "border-[var(--oz-border)] text-[var(--oz-muted)]"}`}>
                    {p.active ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                    {p.active ? "Visible" : "Masquée"}
                  </button>
                  <button data-testid={`ig-save-${p.id}`} onClick={() => save(p)} className="inline-flex items-center gap-1.5 px-3 rounded-lg bg-[var(--oz-dark)] text-white text-xs font-semibold hover:bg-black">
                    <Save className="w-3.5 h-3.5" /> Enreg.
                  </button>
                  <button data-testid={`ig-delete-${p.id}`} onClick={() => remove(p.id)} className="inline-flex items-center px-3 rounded-lg border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
